import type { ApiErrorBody, ApiSuccess } from "@chessla/shared";
import { NIKOLA_TERMINAL_RUN_STATUSES } from "@chessla/shared";
import { getAuthToken } from "./auth";
import { invalidateEntitlements } from "./entitlement-events";

const DEFAULT_API_BASE_URL = "http://localhost:3001/api";

/**
 * The backend deliberately serves application routes below `/api`, while
 * `/health` remains at the origin. Keep the client defensive against an
 * environment value that provides only the API origin.
 */
export const normalizeApiBaseUrl = (value = DEFAULT_API_BASE_URL): string => {
  const baseUrl = value.replace(/\/+$/, "");
  return baseUrl.endsWith("/api") ? baseUrl : `${baseUrl}/api`;
};

export const apiBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export const reportedApiError = (message: string, code: string, status: number): ApiRequestError => {
  const error = new ApiRequestError(message, code, status);
  if ([402, 403, 409, 429].includes(status)) invalidateEntitlements();
  return error;
};

export const apiErrorFromResponse = async (response: Response): Promise<ApiRequestError> => {
  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
  return reportedApiError(
    body?.error?.message ?? "The request could not be completed. Please try again.",
    body?.error?.code ?? "UNKNOWN_ERROR",
    response.status,
  );
};

/**
 * Thin fetch wrapper matching the backend's `{ data }` / `{ error }`
 * response envelope (see coding-standards.md).
 *
 * Automatically attaches the Cognito ID token as a Bearer header on every
 * request — no cookies, no session state in the browser.
 */
export const apiFetch = async (path: string, init?: RequestInit): Promise<Response> => {
  const token = await getAuthToken();
  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers,
  });
};

export const apiClient = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await apiFetch(path, init);

  if (!response.ok) throw await apiErrorFromResponse(response);
  const body = (await response.json()) as ApiSuccess<T> | ApiErrorBody;
  if ("error" in body) {
    throw reportedApiError(body.error.message, body.error.code, response.status);
  }

  const data = body.data;
  if (
    path.startsWith("/entitlements/trial/") ||
    path.startsWith("/engine/analysis/") ||
    path.includes("/onboarding/complete") ||
    (path.startsWith("/coach/runs") &&
      data != null && typeof data === "object" && "status" in data &&
      NIKOLA_TERMINAL_RUN_STATUSES.some((status) => status === data.status)) ||
    (data != null && typeof data === "object" && "creditsCharged" in data)
  )
    invalidateEntitlements();
  return data;
};
